/**
 * O portal do cliente.
 *
 * O espelho do portal da contraparte, e as duas coisas não podem convergir: lá
 * a pessoa vê **apenas a lista do que precisa enviar**; aqui ela vê **o que
 * comprou** — o parecer, o dossiê, a planilha de validades e o semáforo do caso.
 *
 * O que fica de fora é tão deliberado quanto o que entra. Custo interno,
 * chamadas de fornecedor, trilha de auditoria: isso é a operação do escritório,
 * não o produto do cliente, e um portal que mostrasse a margem de quem o
 * atende seria um portal que ninguém liga.
 */
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'

import BlankLayout from '@layouts/blank-layout'
import { getSystemMode } from '@core/utils/server-helpers'
import CustomChip from '@core/components/mui/chip'
import Logo from '@components/layout/shared/logo'

import { CERTIFICATE_STATE_LOOK } from '@/lib/due-diligence'
import { useTRPC } from '@/lib/trpc'

export const Route = createFileRoute('/delivery/$token')({
  loader: () => ({ systemMode: getSystemMode() }),
  component: ClientPortal
})

const date = (value: string | Date | null) => (value ? new Date(value).toLocaleDateString('pt-BR') : '—')

function ClientPortal() {
  const { token } = Route.useParams()
  const { systemMode } = Route.useLoaderData()
  const trpc = useTRPC()

  const { data, isPending, isError, error } = useQuery({ ...trpc.delivery.open.queryOptions({ token }), retry: false })

  // A valid link to a case nobody signed yet is not an expired link: saying so
  // keeps the client from asking for a new one that would not open either.
  const notReleased = error?.data?.code === 'PRECONDITION_FAILED'

  return (
    <BlankLayout systemMode={systemMode}>
      <div className='flex flex-col items-center justify-center min-bs-[100dvh] plb-8 pli-6'>
        <div className='is-full max-is-[900px] flex flex-col gap-6'>
          <div className='flex justify-center'>
            <Logo />
          </div>

          {isPending && (
            <div className='flex justify-center plb-12'>
              <CircularProgress />
            </div>
          )}

          {/*
            Link errado e link vencido respondem igual, como no portal da
            contraparte: um link inválido não diz a quem o segura se já existiu.
          */}
          {isError && (
            <Alert severity='warning'>
              {notReleased
                ? 'O dossiê ainda não foi liberado: falta a assinatura do advogado responsável. Tente de novo mais tarde.'
                : 'Este link não vale mais. Peça um novo a quem preparou a diligência.'}
            </Alert>
          )}

          {data && (
            <>
              <Card>
                <CardContent className='flex flex-col gap-3'>
                  <div className='flex flex-wrap items-start justify-between gap-3'>
                    <div>
                      <Typography variant='h4'>{data.projectName}</Typography>
                      <Typography color='text.secondary'>{data.address}</Typography>
                    </div>
                    <CustomChip round='true' variant='tonal' color='primary' label={data.verdict} />
                  </div>

                  {data.verdictReasons.length > 0 && (
                    <ul className='flex flex-col gap-1 pis-4'>
                      {data.verdictReasons.map(reason => (
                        <li key={reason}>
                          <Typography variant='body2' color='text.secondary'>
                            {reason}
                          </Typography>
                        </li>
                      ))}
                    </ul>
                  )}

                  <Typography variant='caption' color='text.disabled'>
                    Para {data.recipientName} · este link vale até {date(data.expiresAt)}
                  </Typography>
                </CardContent>
              </Card>

              <Card>
                <CardContent className='flex flex-wrap items-center justify-between gap-4'>
                  <div>
                    <Typography variant='h6'>Seus documentos</Typography>
                    <Typography variant='body2' color='text.secondary'>
                      O dossiê reúne o parecer e as certidões, com a impressão digital de cada arquivo. A planilha diz o
                      que vence antes do fechamento.
                      {data.linksReport && ' O relatório de vínculos mostra quem aparece no quadro societário de quem.'}
                    </Typography>
                    {/*
                      O portal só abre caso liberado, então sempre há quem
                      responda pelo parecer — e o cliente vê quem é.
                    */}
                    {data.opinionSignedAt && (
                      <Typography variant='caption' color='text.secondary'>
                        Parecer assinado{data.opinionSignedByName ? ` por ${data.opinionSignedByName}` : ''}
                        {data.opinionSignedByOab ? ` (${data.opinionSignedByOab})` : ''} em {date(data.opinionSignedAt)}.
                      </Typography>
                    )}
                  </div>

                  <div className='flex flex-wrap gap-3'>
                    <Button
                      variant='contained'
                      href={`/files/delivery/${token}/dossier`}
                      startIcon={<i className='tabler-file-download' />}
                    >
                      Baixar o dossiê
                    </Button>
                    <Button
                      variant='tonal'
                      href={`/files/delivery/${token}/sheet`}
                      startIcon={<i className='tabler-table-export' />}
                    >
                      Planilha de validades
                    </Button>
                    {data.linksReport && (
                      <Button
                        variant='tonal'
                        href={`/files/delivery/${token}/links`}
                        startIcon={<i className='tabler-affiliate' />}
                      >
                        Relatório de vínculos
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className='flex flex-col gap-3'>
                  <div className='flex flex-wrap items-baseline justify-between gap-2'>
                    <Typography variant='h6'>Certidões do caso</Typography>
                    {data.expiringSoon > 0 && (
                      <CustomChip
                        round='true'
                        size='small'
                        variant='tonal'
                        color='warning'
                        label={`${data.expiringSoon} vence(m) em até 30 dias`}
                      />
                    )}
                  </div>

                  <div className='overflow-x-auto'>
                    <table className='is-full'>
                      <thead>
                        <tr>
                          {['Documento', 'Pesquisado', 'Situação', 'Emitida', 'Vence'].map(header => (
                            <th key={header} className='text-start plb-2 pie-4'>
                              <Typography variant='caption' color='text.disabled'>
                                {header}
                              </Typography>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {data.certificates.map((certificate, index) => (
                          <tr key={`${certificate.name}-${certificate.subjectName}-${index}`}>
                            <td className='plb-2 pie-4'>
                              <Typography variant='body2'>{certificate.name}</Typography>
                            </td>
                            <td className='plb-2 pie-4'>
                              <Typography variant='body2' color='text.secondary'>
                                {certificate.subjectName}
                              </Typography>
                            </td>
                            <td className='plb-2 pie-4'>
                              {/* The state the server decided, divergence included — the
                                  client must not read "Obtida" where the case screen says otherwise. */}
                              <div className='flex flex-col items-start gap-1'>
                                <CustomChip
                                  round='true'
                                  size='small'
                                  variant='tonal'
                                  color={CERTIFICATE_STATE_LOOK[certificate.state].color}
                                  label={CERTIFICATE_STATE_LOOK[certificate.state].label}
                                />
                                <Typography variant='caption' color='text.secondary'>
                                  {certificate.situation}
                                </Typography>
                              </div>
                            </td>
                            <td className='plb-2 pie-4'>
                              <Typography variant='body2' color='text.secondary'>
                                {certificate.issuedOn ?? '—'}
                              </Typography>
                            </td>
                            <td className='plb-2'>
                              <Typography variant='body2' color='text.secondary'>
                                {date(certificate.expiresOn)}
                              </Typography>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </BlankLayout>
  )
}
